/* Playo scheduler — pure helpers (no React, no API).

   Every schedule time is stored as UTC on the server. The UI shows and edits
   times in the CHANNEL's timezone (an IANA zone such as "Asia/Kolkata"), so
   everything here converts between "a date + minutes in that zone" and UTC
   instants (ms) with Intl only — no date library. DST is handled: a local
   time that doesn't exist (spring forward) resolves to the next valid
   instant, an ambiguous one (fall back) to the first. */

export const MINUTE = 60 * 1000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const SNAP_MS = 15 * MINUTE; // auto-snap reach: start within 15 min after an item's end → snap to it
export const MAX_ITEM_SEC = 24 * 60 * 60; // API limit per item
export const MAX_BULK = 500; // API limit per bulk call

const ms = (v) => (v instanceof Date ? v.getTime() : typeof v === "number" ? v : new Date(v).getTime());

const dtfCache = new Map();
function dtf(tz) {
  if (!dtfCache.has(tz)) {
    dtfCache.set(
      tz,
      new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    );
  }
  return dtfCache.get(tz);
}

export function isValidTimezone(tz) {
  try {
    dtf(tz);
    return true;
  } catch {
    return false;
  }
}

/** Wall-clock parts of instant `t` in `tz`. */
export function zonedParts(t, tz = "UTC") {
  const p = {};
  for (const x of dtf(tz).formatToParts(new Date(ms(t)))) p[x.type] = x.value;
  const hour = Number(p.hour) % 24;
  return {
    date: `${p.year}-${p.month}-${p.day}`,
    hour,
    minute: Number(p.minute),
    second: Number(p.second),
    minutes: hour * 60 + Number(p.minute),
  };
}

/** Offset of `tz` at instant `t`: local wall clock minus UTC, in ms. */
export function tzOffsetMs(t, tz = "UTC") {
  const p = zonedParts(t, tz);
  const [y, m, d] = p.date.split("-").map(Number);
  const asUtc = Date.UTC(y, m - 1, d, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms(t) / 1000) * 1000;
}

/** UTC instant (ms) of `minutes` after local midnight of `dateStr` (YYYY-MM-DD) in `tz`. */
export function zonedToUtc(dateStr, minutes, tz = "UTC") {
  const [y, m, d] = dateStr.split("-").map(Number);
  const wall = Date.UTC(y, m - 1, d) + minutes * MINUTE;
  const first = wall - tzOffsetMs(wall, tz);
  const second = wall - tzOffsetMs(first, tz);
  if (first === second) return first;
  // DST edge: pick the earliest candidate that maps back to the same wall time, else the later one.
  const back = (c) => zonedParts(c, tz);
  const want = { date: dateStr, minutes };
  const ok = (c) => back(c).date === want.date && back(c).minutes === want.minutes;
  if (ok(Math.min(first, second))) return Math.min(first, second);
  if (ok(Math.max(first, second))) return Math.max(first, second);
  return Math.max(first, second); // nonexistent local time → after the gap
}

/** YYYY-MM-DD + n days (calendar arithmetic, zone-free). */
export function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Days between two YYYY-MM-DD strings (b - a). */
export function daysBetween(a, b) {
  const t = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((t(b) - t(a)) / DAY);
}

/** Monday of the week that contains dateStr. */
export function weekStart(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return addDays(dateStr, -((dow + 6) % 7));
}

/** [start, end) of a local day in UTC ms (23/25 h on DST days). */
export function dayRangeUtc(dateStr, tz = "UTC") {
  return { start: zonedToUtc(dateStr, 0, tz), end: zonedToUtc(addDays(dateStr, 1), 0, tz) };
}

export function todayIn(tz = "UTC", now = Date.now()) {
  return zonedParts(now, tz).date;
}

const pad = (n) => String(n).padStart(2, "0");

/** "HH:mm" in tz. */
export function formatTime(t, tz = "UTC") {
  const p = zonedParts(t, tz);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

export function formatDuration(sec) {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${pad(m)}m`;
  if (m) return r ? `${m}m ${pad(r)}s` : `${m}m`;
  return `${r}s`;
}

/** "HH:mm" → minutes, or null. */
export function parseHm(value) {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(value || "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  const s = Number(m[3] || 0);
  if (h > 23 || mi > 59 || s > 59) return null;
  return h * 60 + mi + s / 60;
}

export const toIso = (t) => new Date(ms(t)).toISOString().replace(".000Z", "Z");

/* ─────────────────────────── schedule helpers ─────────────────────────── */

/** Items as { ...item, start, end } in ms, sorted. */
export function normalizeItems(items = []) {
  return items
    .map((it) => {
      const start = ms(it.start_time);
      const end = it.end_time ? ms(it.end_time) : start + Number(it.duration_sec) * 1000;
      return { ...it, start, end };
    })
    .sort((a, b) => a.start - b.start || (a.id || 0) - (b.id || 0));
}

export const overlapsAny = (start, end, items, ignoreId = null) =>
  items.some((it) => (ignoreId == null || it.id !== ignoreId) && start < it.end && it.start < end);

/** Gaps (no item) inside [from, to). */
export function findGaps(items, from, to) {
  const out = [];
  let cursor = from;
  for (const it of normalizeItems(items)) {
    if (it.end <= from || it.start >= to) continue;
    if (it.start > cursor) out.push({ start: cursor, end: Math.min(it.start, to) });
    cursor = Math.max(cursor, it.end);
  }
  if (cursor < to) out.push({ start: cursor, end: to });
  return out.filter((g) => g.end - g.start >= 1000);
}

/**
 * Auto-snap a start time:
 *  - inside an item → right after it (chained through back-to-back items);
 *  - within SNAP_MS after the previous item's end → that end (no tiny gap).
 * `ignoreId` = the item being moved.
 */
export function snapStart(t, items, { ignoreId = null, snapMs = SNAP_MS } = {}) {
  const list = normalizeItems(items).filter((it) => it.id !== ignoreId);
  let start = ms(t);
  let moved = true;
  while (moved) {
    moved = false;
    for (const it of list) {
      if (start >= it.start && start < it.end) {
        start = it.end;
        moved = true;
      }
    }
  }
  const prevEnd = list.filter((it) => it.end <= start).reduce((m, it) => Math.max(m, it.end), -Infinity);
  if (start - prevEnd > 0 && start - prevEnd <= snapMs) start = prevEnd;
  return start;
}

/** When the previous item ends (for "add after previous"), or null. */
export function lastEndBefore(t, items) {
  const ends = normalizeItems(items).filter((it) => it.end <= ms(t)).map((it) => it.end);
  return ends.length ? Math.max(...ends) : null;
}

/** Bulk payload shape for one item copied by `deltaMs`. */
function copyPayload(it, deltaMs) {
  const p = {
    type: it.type || "vod",
    start_time: toIso(it.start + deltaMs),
    duration_sec: Math.round((it.end - it.start) / 1000),
  };
  if (it.asset_id != null) p.asset_id = it.asset_id;
  if (it.title_override) p.title_override = it.title_override;
  return p;
}

/**
 * Copies of `source` items, one batch per shift. A shift maps an item's
 * start (ms) to the copy's start; duration is kept. Copies that would overlap
 * an existing item (or each other) are skipped and counted.
 * Returns { items: [payload], skipped }.
 */
export function planCopies(source, shifts, existing = []) {
  const taken = normalizeItems(existing).map(({ start, end }) => ({ start, end, id: null }));
  const out = [];
  let skipped = 0;
  for (const shift of shifts) {
    const fn = typeof shift === "function" ? shift : (t) => t + shift;
    for (const it of normalizeItems(source)) {
      const start = fn(it.start);
      const end = start + (it.end - it.start);
      if (overlapsAny(start, end, taken)) {
        skipped += 1;
        continue;
      }
      taken.push({ start, end, id: null });
      out.push(copyPayload(it, start - it.start));
    }
  }
  return { items: out, skipped };
}

/**
 * Shifts that copy items of local day `fromDate` onto each target day in
 * `tz`, keeping each item's LOCAL wall-clock start (09:00 stays 09:00 across
 * DST changes). Items are expected to start on `fromDate`.
 */
export function dayDeltas(fromDate, targetDates, tz = "UTC") {
  return targetDates.map((target) => (t) => {
    const p = zonedParts(t, tz);
    const offsetDays = daysBetween(fromDate, p.date); // 0 for items starting on the source day
    const sub = ms(t) % 1000;
    return zonedToUtc(addDays(target, offsetDays), p.minutes + p.second / 60, tz) + sub;
  });
}

/** Target days for "repeat daily/weekly until": every `stepDays` after `fromDate`, through `untilDate`. */
export function repeatDates(fromDate, stepDays, untilDate, max = 366) {
  const out = [];
  for (let d = addDays(fromDate, stepDays); daysBetween(d, untilDate) >= 0 && out.length < max; d = addDays(d, stepDays)) {
    out.push(d);
  }
  return out;
}

/** Filler items for every gap (split at 24 h), skipping gaps under a second. */
export function planFillGaps(gaps, fillerAssetId) {
  const out = [];
  for (const g of gaps) {
    let s = g.start;
    while (g.end - s >= 1000) {
      const dur = Math.min(Math.floor((g.end - s) / 1000), MAX_ITEM_SEC);
      out.push({ type: "filler", asset_id: fillerAssetId, start_time: toIso(s), duration_sec: dur });
      s += dur * 1000;
    }
  }
  return out;
}

/**
 * Lay picked videos back to back from `startMs`, in the order given.
 * entries: [{ video_id, name, duration_sec }] (from resolveSelection).
 * Videos with no known length (or longer than one item may be) can't be
 * placed and come back in `skipped`; the rest keep going without a gap.
 * Returns { items (ready for bulkCreate), placed: [{ ...entry, start, end }], skipped, end }.
 */
export function planBlock(entries, startMs) {
  const items = [];
  const placed = [];
  const skipped = [];
  let t = ms(startMs);
  for (const e of entries || []) {
    const dur = Math.round(Number(e?.duration_sec));
    if (!e?.video_id || !Number.isFinite(dur) || dur <= 0) {
      skipped.push({ ...e, reason: "unknown length" });
      continue;
    }
    if (dur > MAX_ITEM_SEC) {
      skipped.push({ ...e, reason: "longer than 24 hours" });
      continue;
    }
    items.push({ type: "vod", asset_id: e.video_id, start_time: toIso(t), duration_sec: dur });
    placed.push({ ...e, start: t, end: t + dur * 1000 });
    t += dur * 1000;
  }
  return { items, placed, skipped, end: t };
}

/** First existing item that overlaps [start, end), or null. */
export function firstOverlap(items, start, end) {
  return normalizeItems(items).find((it) => it.start < end && start < it.end) || null;
}

export function chunk(list, size = MAX_BULK) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/** { now, next } items at instant t (now = null in a gap). */
export function pickNowNext(items, t = Date.now()) {
  const list = normalizeItems(items);
  const at = ms(t);
  const now = list.find((it) => it.start <= at && at < it.end) || null;
  const next = list.find((it) => it.start > at) || null;
  return { now, next };
}

/** Pixel layout of the items of one local day: [{ item, top, height, clippedStart, clippedEnd }]. */
export function layoutDay(items, dateStr, tz, pxPerMin) {
  const { start, end } = dayRangeUtc(dateStr, tz);
  return normalizeItems(items)
    .filter((it) => it.end > start && it.start < end)
    .map((it) => {
      const s = Math.max(it.start, start);
      const e = Math.min(it.end, end);
      return {
        item: it,
        top: ((s - start) / MINUTE) * pxPerMin,
        height: Math.max(((e - s) / MINUTE) * pxPerMin, 2),
        clippedStart: it.start < start,
        clippedEnd: it.end > end,
      };
    });
}

/** Instant at a pixel offset into a local day's column (rounded to `roundMin` minutes). */
export function instantAtOffset(dateStr, tz, offsetPx, pxPerMin, roundMin = 5) {
  const { start, end } = dayRangeUtc(dateStr, tz);
  const minutes = Math.max(0, offsetPx / pxPerMin);
  const rounded = Math.round(minutes / roundMin) * roundMin;
  return Math.min(start + rounded * MINUTE, end - MINUTE);
}
